import {
  deleteMfaMethod,
  freshUntil,
  getMfaMethods,
  MfaMethod,
  patchMfaMethod,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRestoreFocusTarget } from "@fluentui/react-components";

export function useMfaSettings() {
  const { lang, user, setMfaSetupState, onMfaMethodCreatedRef, mfaSetupState } =
    useContext(PspContext);
  const isTablet = useIsTablet();
  const navigate = useNavigate();

  const restoreFocusTargetAttribute = useRestoreFocusTarget();

  const checkTokenFreshnessIntervalRef = useRef<NodeJS.Timeout>(null);

  const [mfaMethods, setMfaMethods] = useState<MfaMethod[]>();
  const [methodPopoverOpenId, setMethodPopoverOpenId] = useState<string | null>(
    null,
  );
  const [wantsToEditMethod, setWantsToEditMethod] = useState<MfaMethod | null>(
    null,
  );
  const [tempLabel, setTempLabel] = useState<string>();
  const [tempActive, setTempActive] = useState<boolean>();
  const [tempPrimary, setTempPrimary] = useState<boolean>();

  const [wantsToDeleteMethod, setWantsToDeleteMethod] =
    useState<MfaMethod | null>(null);

  const [hasFreshToken, setHasFreshToken] = useState<boolean>();

  // check if fresh token is provided
  useEffect(() => {
    if (checkTokenFreshnessIntervalRef.current) {
      clearInterval(checkTokenFreshnessIntervalRef.current);
    }

    function checkIsFresh() {
      freshUntil().then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          const freshUntilDate = new Date(data).getTime();
          const remaining = freshUntilDate - Date.now();
          setHasFreshToken(remaining > 120_000);
        }
      });
    }

    checkTokenFreshnessIntervalRef.current = setInterval(() => {
      checkIsFresh();
    }, 10_000);

    // execute once on mount
    checkIsFresh();

    return function () {
      if (checkTokenFreshnessIntervalRef.current) {
        clearInterval(checkTokenFreshnessIntervalRef.current);
        checkTokenFreshnessIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // get mfa methods
  useEffect(() => {
    getMfaMethods().then(({ data, error }) => {
      if (error) {
        toast(translate("couldntLoadMfaMethods", lang));
      } else {
        setMfaMethods(
          // this is to have primary methods at the top
          data.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
        );
      }
    });
  }, [lang]);

  useEffect(() => {
    if (onMfaMethodCreatedRef) {
      onMfaMethodCreatedRef.current = (method: MfaMethod) => {
        setMfaMethods((prev) => (prev ? [...prev, method] : [method]));
      };
    }
    return function () {
      if (onMfaMethodCreatedRef) {
        onMfaMethodCreatedRef.current = null;
      }
    };
  }, [onMfaMethodCreatedRef]);

  function onDeleteMfaMethod() {
    if (wantsToDeleteMethod) {
      deleteMfaMethod({ path: { mfa_method_id: wantsToDeleteMethod.id } }).then(
        ({ error, status }) => {
          if (error) {
            // TODO: 400 is incorrect, but it is what it is for now
            if (status === 400) {
              if (wantsToDeleteMethod.isPrimary) {
                toast(translate("youCannotDeletePrimaryMfaMethod", lang));
              } else {
                toast(translate("youCannotDeleteTheLastMfaMethod", lang));
              }
            } else {
              toast(translate("couldntDelete2faMethod", lang));
            }
          } else {
            toast(
              translate("mfaMethodDeletedSuccessfully", lang, {
                "{methodLabel}": wantsToDeleteMethod.label,
              }),
            );
            setMfaMethods((prev) =>
              prev?.filter((method) => method.id !== wantsToDeleteMethod.id),
            );
            setWantsToDeleteMethod(null);
          }
        },
      );
    }
  }

  function startEditingMethod(method: MfaMethod) {
    setMethodPopoverOpenId(null);
    setWantsToEditMethod(method);
    setTempLabel(method.label);
    setTempActive(method.isActive);
    setTempPrimary(method.isPrimary);
  }

  function stopEditingMethod() {
    setWantsToEditMethod(null);
    setTimeout(() => {
      setTempLabel(undefined);
      setTempActive(undefined);
      setTempPrimary(undefined);
    }, 100);
  }

  const somethingChanged =
    wantsToEditMethod?.isActive !== tempActive ||
    wantsToEditMethod?.isPrimary !== tempPrimary ||
    wantsToEditMethod?.label !== tempLabel;

  function onSaveEdit() {
    if (wantsToEditMethod && somethingChanged) {
      patchMfaMethod({
        path: { mfa_method_id: wantsToEditMethod.id },
        body: {
          isActive: tempActive,
          isPrimary: tempPrimary,
          label: tempLabel,
        },
      }).then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setMfaMethods((prev) =>
            prev?.map((method) =>
              method.id === data.id
                ? data
                : data.isPrimary
                  ? { ...method, isPrimary: false }
                  : method,
            ),
          );
          toast(translate("mfaMethodEditedSuccessfully", lang));
          stopEditingMethod();
        }
      });
    }
  }

  function navigateToLogin() {
    navigate("/login", { state: { from: "/settings", reauth: true } });
  }

  function startMfaSetup() {
    setMfaSetupState("firstSetup");
  }

  return {
    lang,
    user,
    mfaMethods,
    methodPopoverOpenId,
    setMethodPopoverOpenId,
    wantsToEditMethod,
    wantsToDeleteMethod,
    setWantsToDeleteMethod,
    onDeleteMfaMethod,
    tempLabel,
    setTempLabel,
    startEditingMethod,
    stopEditingMethod,
    tempActive,
    tempPrimary,
    setTempActive,
    setTempPrimary,
    onSaveEdit,
    somethingChanged,
    setMfaSetupState,
    isTablet,
    hasFreshToken,
    navigateToLogin,
    startMfaSetup,
    restoreFocusTargetAttribute,
  };
}
